<script setup>
// GPS-or-PIN body: segmented toggle over the GPS and PIN bodies — the user
// completes whichever they can.
// Port of `GpsOrPinBody` in the source `_shared/CheckInSheet.tsx`.
import { ref } from 'vue'
import { MapPin, Lock } from '@lucide/vue'
import GpsBody from './GpsBody.vue'
import PinBody from './PinBody.vue'

defineProps({
  spec: { type: Object, required: true },
  // Debug hook threaded to the GPS step (see GpsBody).
  forceState: { type: String, default: null },
  // Program-config recovery copy override, threaded to the GPS step.
  recoveryCopy: { type: Object, default: null },
  // Live validation handler, threaded to both bodies — either factor alone
  // completes an OR check-in, so each submits its own payload in full.
  submit: { type: Function, default: null },
})

defineEmits(['success', 'help'])

const tab = ref('gps')
</script>

<template>
  <div class="cs-gps-or-pin">
    <div class="cs-gps-or-pin__tabs">
      <button
        id="checkin-gps-or-pin-tab-gps"
        type="button"
        class="cs-gps-or-pin__tab gtm-click"
        :class="tab === 'gps' ? 'cs-gps-or-pin__tab--active' : 'cs-gps-or-pin__tab--muted'"
        @click="tab = 'gps'"
      >
        <MapPin class="cs-gps-or-pin__tab-icon" :size="12" /> Use GPS
      </button>
      <button
        id="checkin-gps-or-pin-tab-pin"
        type="button"
        class="cs-gps-or-pin__tab gtm-click"
        :class="tab === 'pin' ? 'cs-gps-or-pin__tab--active' : 'cs-gps-or-pin__tab--muted'"
        @click="tab = 'pin'"
      >
        <Lock class="cs-gps-or-pin__tab-icon" :size="12" /> Use PIN
      </button>
    </div>
    <!-- GPS recovery's "Use venue PIN instead" lands on the PIN tab. -->
    <GpsBody
      v-if="tab === 'gps'"
      :spec="spec"
      :force-state="forceState"
      :recovery-copy="recoveryCopy"
      :submit="submit"
      @success="$emit('success')"
      @use-pin="tab = 'pin'"
      @help="$emit('help')"
    />
    <PinBody v-else :spec="spec" :submit="submit" @success="$emit('success')" />
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `space-y-2 pt-1`
.cs-gps-or-pin {
  padding-top: 4px; // pt-1

  > * + * {
    margin-top: 8px; // space-y-2
  }

  // Source: `mx-auto inline-flex rounded-full bg-current/5 p-1 text-[10px]
  //          font-bold uppercase tracking-wider`
  &__tabs {
    margin-left: auto;
    margin-right: auto;
    display: inline-flex;
    border-radius: 9999px;
    background: cs-current(5%);
    padding: 4px; // p-1
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  // Source: `rounded-full px-3 py-1.5` + active `${theme.primaryBtn}` / inactive `${theme.muted}`
  &__tab {
    @include cs-button-reset;
    border-radius: 9999px;
    padding: 6px 12px; // px-3 py-1.5
    font-size: inherit;
    font-weight: inherit;
    text-transform: inherit;
    letter-spacing: inherit;

    &--active {
      background: var(--fr-primary, #FF9900);
      color: var(--fr-primary-foreground, #232F3E);
    }

    &--muted {
      color: var(--fr-muted, #6b7280);
    }
  }

  // Source: `mr-1 inline h-3 w-3`
  &__tab-icon {
    margin-right: 4px; // mr-1
    display: inline;
    vertical-align: middle;
  }
}
</style>
